// src/components/files/PieceJointePicker.tsx — SCE Issues
// Sélecteur de pièces jointes pour le formulaire de déclaration.
//
// Comportement :
//  - Deux boutons : "Prendre une photo" (caméra) et "Choisir des fichiers"
//  - Sélection multi-fichiers autorisée
//  - Preview immédiate des images (FileReader) + icône pour PDF/vidéos
//  - Validation taille (25 MB) et extension à la sélection
//  - Messages d'erreur par fichier rejeté
//  - Badge ✕ sur chaque preview pour retirer un fichier de la liste
//
// PJ permanentes (pas de suppression après upload) → on doit retirer AVANT
// soumission. Après, c'est trop tard. Un bandeau "attention" est affiché.
//
// Une photo peut être annotée avant l'envoi (09/10) : « Annoter », puis
// « Modifier ». La version annotée partira avec l'original (envoyerFichiers).
// Une photo qui vient d'être PRISE ouvre l'éditeur d'elle-même ; « Passer »
// la garde telle quelle.
//
// Glisser-déposer (12/10, PC) : les boutons d'ajout sont dans une zone de dépôt
// (hooks/useZoneDepot) qui s'annonce dès qu'un fichier est glissé dans la fenêtre.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Camera, Upload, FileText, Film, X, AlertCircle, PenLine } from 'lucide-react'
import { EditeurAnnotation } from '@/components/files/EditeurAnnotation'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useZoneDepot } from '@/hooks/useZoneDepot'
import {
  INPUT_ACCEPT,
  validateFile,
  FileValidationError,
  type PieceJointe,
} from '@/api/files.api'

// ─── Types ───

export interface FichierEnAttente {
  file: File
  previewUrl: string | null     // null pour PDF et vidéos
  id: string                    // clé React locale (random)
  /** Version annotée avant l'envoi (JPEG), et son aperçu. */
  annotation?: Blob
  apercuAnnotation?: string
}

interface PieceJointePickerProps {
  /** Fichiers déjà sélectionnés (état externe, contrôlé) */
  fichiers: FichierEnAttente[]
  onChange: (next: FichierEnAttente[]) => void
  /** PJ déjà uploadées (en lecture seule) — affichées séparément dans la galerie */
  uploadees?: PieceJointe[]
  /** Désactive toute interaction (ex: pendant la soumission) */
  disabled?: boolean
}

/**
 * Fichiers prêts à être joints (validés, aperçu pour les images). Sert au
 * sélecteur et au collage dans les champs de texte (utils/pressePapier).
 */
export function preparerFichiers(files: File[]): { nouveaux: FichierEnAttente[]; erreurs: string[] } {
  const erreurs: string[] = []
  const nouveaux: FichierEnAttente[] = []
  for (const file of files) {
    try {
      validateFile(file)
      nouveaux.push({
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      })
    } catch (err) {
      erreurs.push(
        err instanceof FileValidationError
          ? err.message
          : `"${file.name}" : erreur de validation`,
      )
    }
  }
  return { nouveaux, erreurs }
}

export function PieceJointePicker({
  fichiers,
  onChange,
  uploadees = [],
  disabled = false,
}: PieceJointePickerProps): JSX.Element {
  const [erreurs, setErreurs] = useState<string[]>([])
  const inputCameraRef = useRef<HTMLInputElement>(null)
  const inputFichierRef = useRef<HTMLInputElement>(null)

  // Nettoyage des ObjectURLs à l'unmount pour éviter les fuites mémoire
  useEffect(() => {
    return () => {
      fichiers.forEach((f) => {
        if (f.previewUrl) URL.revokeObjectURL(f.previewUrl)
        if (f.apercuAnnotation) URL.revokeObjectURL(f.apercuAnnotation)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [enAnnotation, setEnAnnotation] = useState<FichierEnAttente | null>(null)
  const [annotationProposee, setAnnotationProposee] = useState(false)

  const handleFiles = useCallback(
    async (rawFiles: ArrayLike<File> | null, depuisAppareil = false): Promise<void> => {
      if (!rawFiles || rawFiles.length === 0) return
      const { nouveaux, erreurs: errs } = preparerFichiers(Array.from(rawFiles))
      setErreurs(errs)
      if (nouveaux.length > 0) {
        onChange([...fichiers, ...nouveaux])
      }
      // Photo prise à l'instant : l'annotation est proposée tout de suite.
      const prise = nouveaux.length === 1 ? nouveaux[0] : undefined
      if (depuisAppareil && prise?.previewUrl) {
        setAnnotationProposee(true)
        setEnAnnotation(prise)
      }
    },
    [fichiers, onChange],
  )

  function retirer(id: string): void {
    const cible = fichiers.find((f) => f.id === id)
    if (cible?.previewUrl) URL.revokeObjectURL(cible.previewUrl)
    if (cible?.apercuAnnotation) URL.revokeObjectURL(cible.apercuAnnotation)
    onChange(fichiers.filter((f) => f.id !== id))
  }

  function annoter(cible: FichierEnAttente, image: Blob): void {
    if (cible.apercuAnnotation) URL.revokeObjectURL(cible.apercuAnnotation)
    const apercuAnnotation = URL.createObjectURL(image)
    onChange(fichiers.map((f) => (f.id === cible.id ? { ...f, annotation: image, apercuAnnotation } : f)))
    setEnAnnotation(null)
  }

  const totalPJ = uploadees.length + fichiers.length

  // Glisser-déposer (PC, 12/10) : la zone entoure les boutons d'ajout. Un
  // fichier lâché passe par les mêmes contrôles que « Choisir des fichiers ».
  const avecSouris = useMediaQuery('(hover: hover) and (pointer: fine)')
  const { fichierEnVol, survol, propsZone } = useZoneDepot((deposes) => void handleFiles(deposes), !disabled)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Boutons d'ajout, et zone de dépôt autour (souris seulement) */}
      <div
        {...propsZone}
        data-zone-depot=""
        style={{
          position: 'relative',
          ...(avecSouris
            ? {
                padding: 10,
                borderRadius: 12,
                border: '1px dashed var(--color-gray-50)',
              }
            : {}),
        }}
      >
        <div className="grid grid-cols-2 gap-2">
          <PickerButton
            label="Prendre une photo"
            Icon={Camera}
            disabled={disabled}
            onClick={() => inputCameraRef.current?.click()}
          />
          <PickerButton
            label="Choisir des fichiers"
            Icon={Upload}
            disabled={disabled}
            onClick={() => inputFichierRef.current?.click()}
          />
        </div>
        {avecSouris && (
          <p
            style={{
              margin: '8px 0 0',
              textAlign: 'center',
              fontSize: 'var(--font-size-info)',
              color: 'var(--color-text-secondary)',
            }}
          >
            ou glissez-déposez vos fichiers ici
          </p>
        )}

        {/* Un fichier est en train d'être glissé : la zone le dit */}
        {fichierEnVol && !disabled && (
          <div
            aria-hidden="true"
            className="flex flex-col items-center justify-center"
            style={{
              position: 'absolute',
              inset: 0,
              gap: 6,
              borderRadius: 12,
              border: survol ? '2px solid var(--color-black)' : '2px dashed var(--color-black)',
              background: survol ? 'var(--color-yellow)' : 'var(--color-white)',
              color: 'var(--color-black)',
              fontWeight: 700,
              pointerEvents: 'none',
              transition: 'background-color 0.12s',
            }}
          >
            <Upload size={26} strokeWidth={2.2} />
            <span style={{ fontSize: 'var(--font-size-copy2)' }}>
              {survol ? 'Relâchez pour joindre' : 'Déposez vos fichiers ici'}
            </span>
          </div>
        )}
      </div>

      {/* Inputs cachés */}
      <input
        ref={inputCameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => {
          void handleFiles(e.target.files, true)
          // Même photo reprise deux fois de suite : l'événement doit repartir.
          e.target.value = ''
        }}
      />
      <input
        ref={inputFichierRef}
        type="file"
        accept={INPUT_ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => void handleFiles(e.target.files)}
      />

      {/* Erreurs de validation */}
      {erreurs.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {erreurs.map((e, i) => (
            <div
              key={i}
              className="flex items-start gap-2 p-2 rounded"
              style={{
                // Comme les autres erreurs de l'appli : surface blanche,
                // contour et texte rouges — plus de rose pâle hors charte.
                backgroundColor: 'var(--color-white)',
                border: '1px solid var(--color-traffic-red)',
                fontSize: 'var(--font-size-info)',
                color: 'var(--color-traffic-red)',
              }}
            >
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{e}</span>
            </div>
          ))}
        </div>
      )}

      {/* Previews des fichiers en attente */}
      {fichiers.length > 0 && (
        <div>
          <p
            style={{
              fontSize: 'var(--font-size-info)',
              color: 'var(--color-text-secondary)',
              marginBottom: 8,
            }}
          >
            {fichiers.length} fichier{fichiers.length > 1 ? 's' : ''} prêt
            {fichiers.length > 1 ? 's' : ''} à uploader
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 8,
            }}
          >
            {fichiers.map((f) => (
              <PreviewThumbnail
                key={f.id}
                fichier={f}
                onRetirer={() => retirer(f.id)}
                onAnnoter={() => {
                  setAnnotationProposee(false)
                  setEnAnnotation(f)
                }}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      )}

      {enAnnotation && (
        <EditeurAnnotation
          // Déjà annotée : on reprend sur la version annotée.
          source={{ fichier: enAnnotation.annotation ?? enAnnotation.file }}
          mode="envoi"
          {...(annotationProposee ? { libelleAnnuler: 'Passer', libelleSansAnnotation: 'Ne pas annoter' } : {})}
          onAnnuler={() => setEnAnnotation(null)}
          onTerminer={(image) => annoter(enAnnotation, image)}
        />
      )}

      {/* Avertissement "PJ permanentes" si au moins 1 fichier sélectionné */}
      {fichiers.length > 0 && (
        <div
          className="flex gap-2 p-2 rounded"
          style={{
            backgroundColor: 'var(--color-gray-5)',
            border: '1px solid var(--color-border)',
            fontSize: 'var(--font-size-info)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Une fois le problème déclaré, les pièces jointes ne peuvent plus
            être supprimées. Vérifiez vos fichiers avant de valider.
          </span>
        </div>
      )}

      {/* Compteur total */}
      {totalPJ > 0 && (
        <p
          style={{
            fontSize: 'var(--font-size-info)',
            color: 'var(--color-text-secondary)',
            textAlign: 'right',
          }}
        >
          {totalPJ} pièce{totalPJ > 1 ? 's' : ''} jointe
          {totalPJ > 1 ? 's' : ''} au total
        </p>
      )}
    </div>
  )
}

// ─── Sous-composants ───

function PickerButton({
  label,
  Icon,
  onClick,
  disabled,
}: {
  label: string
  Icon: typeof Camera
  onClick: () => void
  disabled: boolean
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center justify-center gap-1.5 py-4 rounded-lg"
      style={{
        backgroundColor: 'var(--color-gray-5)',
        border: '1px dashed var(--color-border)',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <Icon size={22} color="var(--color-gray-60)" />
      <span
        style={{
          fontSize: 'var(--font-size-info)',
          color: 'var(--color-text-secondary)',
        }}
      >
        {label}
      </span>
    </button>
  )
}

function PreviewThumbnail({
  fichier,
  onRetirer,
  onAnnoter,
  disabled,
}: {
  fichier: FichierEnAttente
  onRetirer: () => void
  onAnnoter: () => void
  disabled: boolean
}): JSX.Element {
  const { file } = fichier
  const previewUrl = fichier.apercuAnnotation ?? fichier.previewUrl
  const isPdf = file.type === 'application/pdf'
  const isVideo = file.type.startsWith('video/')

  return (
    <div
      style={{
        position: 'relative',
        aspectRatio: '1',
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: 'var(--color-gray-5)',
        border: '1px solid var(--color-border)',
      }}
    >
      {previewUrl ? (
        <img
          src={previewUrl}
          alt={file.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <div
          className="flex flex-col items-center justify-center w-full h-full"
          style={{ gap: 4 }}
        >
          {isPdf ? (
            <FileText size={28} color="var(--color-traffic-red)" />
          ) : isVideo ? (
            <Film size={28} color="var(--color-gray-70)" />
          ) : (
            <Upload size={28} color="var(--color-gray-60)" />
          )}
          <span
            style={{
              fontSize: 9,
              color: 'var(--color-text-secondary)',
              textAlign: 'center',
              padding: '0 4px',
              wordBreak: 'break-all',
              lineHeight: '12px',
            }}
          >
            {file.name.length > 20 ? `${file.name.slice(0, 18)}…` : file.name}
          </span>
        </div>
      )}

      {/* Bouton ✕ */}
      {!disabled && (
        <button
          type="button"
          onClick={onRetirer}
          aria-label={`Retirer ${file.name}`}
          className="flex items-center justify-center"
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 20,
            height: 20,
            borderRadius: 10,
            backgroundColor: 'rgba(0,0,0,0.65)',
            color: 'white',
            border: 'none',
          }}
        >
          <X size={12} strokeWidth={2.5} />
        </button>
      )}

      {/* Annotée : pastille jaune, comme dans le bloc Photos */}
      {fichier.annotation && (
        <span
          aria-label="Annotée"
          className="flex items-center justify-center"
          style={{
            position: 'absolute',
            top: 4,
            left: 4,
            width: 22,
            height: 22,
            borderRadius: 11,
            background: 'var(--color-yellow)',
            color: 'var(--color-black)',
          }}
        >
          <PenLine size={12} strokeWidth={2.4} />
        </span>
      )}

      {/* Annoter avant l'envoi */}
      {previewUrl && !disabled && (
        <button
          type="button"
          onClick={onAnnoter}
          className="flex items-center justify-center"
          style={{
            position: 'absolute',
            left: 4,
            right: 4,
            bottom: 18,
            height: 30,
            gap: 5,
            borderRadius: 8,
            border: 'none',
            background: 'rgba(255,255,255,0.92)',
            color: 'var(--color-black)',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          {fichier.annotation ? 'Modifier' : (<><PenLine size={13} /> Annoter</>)}
        </button>
      )}

      {/* Indicateur de taille */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'rgba(0,0,0,0.45)',
          padding: '2px 4px',
          fontSize: 9,
          color: 'white',
          textAlign: 'right',
        }}
      >
        {formatSize(file.size)}
      </div>
    </div>
  )
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`
}
